import React, { useState, useEffect } from 'react';
import { Button, TextInput } from '@dsg-tech/homefield-react';

interface StoreSelectionFormProps {
  onSubmit: (storeNumber: number, associateId: string) => void;
  error?: string | null;
  validateOnParentRequest?: boolean;
  onStoreInputChange?: () => void;
}

const StoreSelectionForm: React.FC<StoreSelectionFormProps> = ({
  onSubmit,
  error,
  validateOnParentRequest = false,
  onStoreInputChange,
}) => {
  const [storeInputValue, setStoreInputValue] = useState<string>('');
  const [associateIdValue, setAssociateIdValue] = useState<string>('');
  const [hasInput, setHasInput] = useState<boolean>(false);
  const [localAssociateIdError, setLocalAssociateIdError] = useState<string | null>(null);
  const [localStoreError, setLocalStoreError] = useState<string | null>(null);

  const validateAssociateId = (value: string): boolean => {
    if (!value || value.trim() === '') {
      setLocalAssociateIdError('Employee ID is required. Please enter your employee ID.');
      return false;
    }

    if (value.length !== 7 && value.length !== 10) {
      setLocalAssociateIdError(
        `Employee ID ${value} is invalid. Please Enter a 7 or 10 characters Employee ID`,
      );
      return false;
    }

    // Check if it only contains alphanumeric characters
    const alphanumericRegex = /^[a-zA-Z0-9]+$/;
    if (!alphanumericRegex.test(value)) {
      setLocalAssociateIdError('Employee ID must contain only letters and numbers');
      return false;
    }

    setLocalAssociateIdError(null);
    return true;
  };

  const validateStoreNumber = (value: string): boolean => {
    if (!value || value.trim() === '') {
      setLocalStoreError('Store Number is required. Please enter your store number.');
      return false;
    }

    const storeNum = parseInt(value, 10);
    if (Number.isNaN(storeNum)) {
      setLocalStoreError('Please enter a valid store number');
      return false;
    }

    setLocalStoreError(null);
    return true;
  };

  const validateForm = (submitForm = true): boolean => {
    const isStoreValid = validateStoreNumber(storeInputValue);
    const isAssociateIdValid = validateAssociateId(associateIdValue);

    if (isStoreValid && isAssociateIdValid && submitForm) {
      onSubmit(parseInt(storeInputValue, 10), associateIdValue);
      return true;
    }

    return isStoreValid && isAssociateIdValid;
  };

  useEffect(() => {
    if (validateOnParentRequest) {
      validateForm(false);
    }
  }, [validateOnParentRequest]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    validateForm(true);
  };

  const handleStoreInputChange = (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const { value } = e.target;
    setStoreInputValue(value);
    setHasInput(value.length > 0 && associateIdValue.length > 0);

    if (localStoreError) {
      setLocalStoreError(null);
    }

    if (onStoreInputChange) {
      onStoreInputChange();
    }
  };

  const handleAssociateIdChange = (
    e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    const { value } = e.target;
    setAssociateIdValue(value);
    setHasInput(value.length > 0 && storeInputValue.length > 0);

    if (localAssociateIdError) {
      setLocalAssociateIdError(null);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="store-selection-form hmf-mb-0 hmf-display-flex hmf-flex-column">
      <div className="form-group hmf-position-relative hmf-mb-0 hmf-pb-s">
        <div className="input-border">
          <TextInput
            name="storeNumber"
            id="storeNumber"
            type="number"
            value={storeInputValue}
            onChange={handleStoreInputChange}
            placeholder=" "
            label="Store Number"
            error={!!localStoreError || !!error}
            helperText={localStoreError ?? error ?? undefined}
          />
        </div>
      </div>

      <div className="form-group hmf-position-relative hmf-mb-0 hmf-pb-s">
        <div className="input-border">
          <TextInput
            name="associateId"
            id="associateId"
            type="text"
            value={associateIdValue}
            onChange={handleAssociateIdChange}
            placeholder=" "
            label="Employee ID"
            error={!!localAssociateIdError}
            helperText={localAssociateIdError ?? undefined}
          />
        </div>
      </div>

      <Button
        type="submit"
        variant="primary"
        size="medium"
        disabled={!hasInput}
        text="Submit"
        className={`submit-button ${hasInput ? 'active' : ''}`}
      />
    </form>
  );
};

export default StoreSelectionForm;
